import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, Loader2, User, Lock, Building2, ArrowRight, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CompleteRegistration() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const sessionId = searchParams.get("session_id");
  const emailFromUrl = searchParams.get("email");
  const referralCode = searchParams.get("ref") || localStorage.getItem("ledgeramt_referral_code");
  
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState("");
  
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionId && emailFromUrl) {
      verifySubscription();
    } else {
      setError("Invalid registration link. Please start from the subscription page.");
      setVerifying(false);
    }
  }, [sessionId, emailFromUrl]);

  const verifySubscription = async () => {
    try {
      const response = await axios.get(
        `${API}/subscription/verify-pre-registration/${sessionId}?email=${encodeURIComponent(emailFromUrl)}`
      );
      
      if (response.data.status === "verified") {
        setSubscriptionData(response.data);
        setVerified(true);
      } else if (response.data.status === "completed") {
        toast.info("This subscription is already linked to an account");
        navigate("/login");
      } else {
        setError("Your subscription is still being processed. Please wait a moment and refresh.");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError(err.response?.data?.detail || "Failed to verify subscription");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register-with-subscription`, {
        email: emailFromUrl,
        password: password,
        name: name || emailFromUrl.split("@")[0],
        session_id: sessionId,
        stripe_customer_id: subscriptionData.stripe_customer_id,
        stripe_subscription_id: subscriptionData.stripe_subscription_id,
        plan: subscriptionData.plan,
        current_period_end: subscriptionData.current_period_end || "",
        referral_code: referralCode || ""
      });

      // Clear referral code from localStorage after successful signup
      localStorage.removeItem("ledgeramt_referral_code");

      // Auto-login with the returned token
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.access_token}`;
      
      toast.success("Account created! Welcome to LedgerAMT!");
      navigate("/onboarding");
    } catch (err) {
      console.error("Registration error:", err);
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-4" />
          <p className="text-white/60">Verifying your subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Verification Failed</h1>
          <p className="text-white/60 mb-6">{error}</p>
          <Link to="/pricing">
            <Button className="bg-emerald-500 hover:bg-emerald-600">
              Start Over
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md" data-testid="complete-registration-page">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src="/ledgeramt-logo.png" alt="LedgerAMT" className="h-16 mx-auto mb-4" />
          </Link>
          
          {/* Success Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 mb-4">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-400 font-medium">Payment Successful!</span>
          </div>
          
          <h1 className="text-2xl font-bold text-white">Complete your account</h1>
          <p className="text-white/60 mt-2">
            Your subscription is active. Set up your account to continue.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
          {/* Email Display */}
          <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
            <p className="text-white/60 text-sm mb-1">Subscribed email</p>
            <p className="text-white font-medium">{emailFromUrl}</p>
            {subscriptionData?.current_period_end && (
              <p className="text-emerald-400 text-sm mt-1">
                Valid until: {new Date(subscriptionData.current_period_end).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-white/80">Your name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password (min 6 characters)"
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-lg font-semibold group"
              data-testid="complete-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Building2 className="w-5 h-5 mr-2" />
              )}
              Create Account
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </form>

          <p className="text-white/40 text-xs text-center mt-4">
            By creating an account, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}
